import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import type { FieldKind } from "@storyboard/dispatch-view/fieldKinds";
import { compileDispatchFilter, type DispatchControlValues, hasTimeOperator } from "@storyboard/dispatch-view/query";
import type {
  DispatchViewStatePatch,
  ResolvedDispatchQuery,
  ResolvedDispatchView,
} from "@storyboard/dispatch-view/resolve";
import type { OpenTask } from "@storyboard/dispatch-view/task";
import { tasksForShelf } from "@storyboard/dispatch-view/task";
import type {
  DispatchControl,
  DispatchControlValue,
  DispatchField,
  DispatchSort,
  DispatchViewDefinition,
} from "@storyboard/dispatch-view/view";
import { NONE } from "@storyboard/dispatch-view/view";
import type { FilterNode, ProjectedRecord } from "@storyboard/driver-query/predicate";
import { matchesFilter } from "@storyboard/driver-query/predicate";
import { labelForColumnId } from "@/components/Dispatch/columns";
import { columnIdForConditionField } from "@/components/Dispatch/DispatchColumnMenu";
import type { DisplaySort } from "@/components/Dispatch/DispatchDisplayMenu";
import type { QueryChip } from "@/components/Dispatch/DispatchQueryStrip";
import { ampli } from "@/config";
import type { DriverRow, TagDefinition } from "@/types/driver";
import {
  type ConditionNaming,
  chosenValues,
  conditionField,
  replaceFieldConditions,
  valuesCondition,
} from "./adhocConditions";
import type { DispatchItem } from "./dispatchItems";
import { type DispatchTableRow, driverRowTask, shelfTasksByDriver } from "./dispatchTableRow";
import { type DispatchGroup, groupDispatchRows, toggleCollapsedGroup } from "./groupDispatchRows";
import { projectDispatchItemRecord, projectDispatchRecord, taskFields } from "./projectDispatchRecord";
import { buildQueryChips, clearAllPatch, narrowingCount } from "./queryChips";
import { defaultSortDirection, sortDispatchRows } from "./sortDispatchRows";
import { keepEqualRows } from "./stableRows";
import { shelveTasks, type TaskShelf, withTaskShelf } from "./taskShelf";
import type { TaskFold } from "./tasks";
import { type ValueOption, valueOptions } from "./valueOptions";

/** The rows grouped under one field's values, with the group headers the table draws between them. */
export type GroupedDispatchRows = { groups: DispatchGroup[]; rows: DispatchTableRow[] };

const NO_PREVIOUS: ReadonlyMap<string, DispatchTableRow> = new Map();

const NO_COLUMNS: ReadonlySet<string> = new Set();

/** The shelf while the board has no open task on it. */
const NO_SHELF: TaskShelf = { rows: [], tasksByRow: new Map() };

/**
 * The filter a search term compiles to: the term over the view's search fields alone, with every control and ad-hoc
 * condition left out, so a search finds any driver the member can see whatever the filters hide. The filters stay in
 * the view's state untouched, so emptying the box brings them straight back.
 *
 * @param query - The resolved query, whose `search` names the fields the term scans.
 * @param search - The term in the search box; blank compiles to match-all (`null`).
 */
export function compileSearchFilter(
  query: Pick<ResolvedDispatchQuery, "controlValues" | "search">,
  search: string,
): FilterNode | null {
  return compileDispatchFilter(
    { controls: [], controlValues: query.controlValues, search: query.search, adhoc: [] },
    search,
  );
}

/**
 * The rows a filter keeps, plus the driver whose popover is open on a driver-per-row view, so acknowledging from
 * History never pulls the row out from under the panel.
 */
function keepMatching(
  rows: DispatchTableRow[],
  filterNode: FilterNode | null,
  openMembershipId: string | undefined,
  now: number | null,
): DispatchTableRow[] {
  if (!filterNode) return rows;
  return rows.filter(
    (row) =>
      (row.kind === "driver" && row.driver.workspaceMembershipId === openMembershipId) ||
      matchesFilter(filterNode, row.record, now ?? undefined),
  );
}

const rowId = (row: DispatchTableRow) => row.id;

/**
 * Projects the store's drivers (or their messages, at the message grain) to table rows, each joined to its driver and
 * flattened once so filters, sort, and cells agree.
 *
 * A row in `previous` built from the same driver object (and item, at the message grain), the same waiting count, and
 * the same task fields is returned as is rather than projected again. The caller passes `previous` only while `grain`
 * and `tagIdBySlug` hold. A driver row carries the task its chip shows ({@link driverRowTask}); a message row, its
 * item's.
 *
 * @param grain - Whether a row is a driver or one message.
 * @param rows - The store's drivers.
 * @param items - The messages, events, and tasks pinned to other boards at the message grain; ignored at the driver
 *   grain.
 * @param tagIdBySlug - The workspace's tags, slug to id, each projected to a `tag:<slug>` key.
 * @param unacknowledgedByFeed - How many things wait on each channel.
 * @param tasks - The workspace's open tasks; none while tasks are off.
 * @param previous - Last pass's rows by id, reused where nothing they were built from changed.
 * @param shelf - This board's shelf tasks, which decide the task a Pinned Tasks driver row carries.
 */
export function projectRows(
  grain: ResolvedDispatchView["grain"],
  rows: readonly DriverRow[],
  items: readonly DispatchItem[],
  tagIdBySlug: ReadonlyMap<string, string>,
  unacknowledgedByFeed: ReadonlyMap<string, number>,
  tasks: TaskFold,
  previous: ReadonlyMap<string, DispatchTableRow> = NO_PREVIOUS,
  shelf: readonly OpenTask[] = [],
): DispatchTableRow[] {
  const waitingCount = (driver: DriverRow) =>
    driver.feedId != null ? (unacknowledgedByFeed.get(driver.feedId) ?? 0) : 0;
  if (grain === "message") {
    // Each driver projects once; every message on their channel spreads that base under its own message fields.
    const driversById = new Map(rows.map((driver) => [driver.workspaceMembershipId, driver]));
    const baseByDriver = new Map<string, ProjectedRecord>();
    return items.flatMap((item): DispatchTableRow[] => {
      const driver = driversById.get(item.driverMembershipId);
      if (!driver) return [];
      // A message row reads its acknowledgement from the item, so its driver and item are all it depends on.
      const prior = previous.get(item.id);
      if (prior?.kind === "message" && prior.item === item && prior.driver === driver) return [prior];
      let base = baseByDriver.get(item.driverMembershipId);
      if (!base) {
        base = projectDispatchRecord(driver, tagIdBySlug, waitingCount(driver) > 0);
        baseByDriver.set(item.driverMembershipId, base);
      }
      return [{ kind: "message", id: item.id, item, driver, record: projectDispatchItemRecord(base, driver, item) }];
    });
  }
  const shelfByDriver = shelfTasksByDriver(shelf);
  return rows.map((driver) => {
    const unacknowledgedCount = waitingCount(driver);
    const id = driver.workspaceMembershipId;
    const task = driverRowTask(id, tasks, shelfByDriver.get(id)) ?? undefined;
    const prior = previous.get(driver.workspaceMembershipId);
    if (
      prior?.kind === "driver" &&
      prior.driver === driver &&
      prior.unacknowledgedCount === unacknowledgedCount &&
      sameTaskFields(prior.record, task)
    ) {
      return prior;
    }
    return {
      kind: "driver",
      id: driver.workspaceMembershipId,
      driver,
      unacknowledgedCount,
      record: projectDispatchRecord(driver, tagIdBySlug, unacknowledgedCount > 0, task),
    };
  });
}

/** Whether a projected driver record already carries the task's fields, so the row need not be projected again. */
function sameTaskFields(record: ProjectedRecord, task: OpenTask | undefined): boolean {
  const expected = taskFields(task, "task_view", "task_author", "task_at");
  return Object.entries(expected).every(([key, value]) => record[key] === value);
}

/**
 * The rows the resolved view yields from the store, and every way the query over them changes: project, then sort,
 * then filter, then group.
 *
 * Owns the transient search term (kept with the tab, cleared when the view changes), which sets every filter aside
 * while it holds text: the table shows the term's matches across all rows, and the filters come back unchanged once it
 * empties. Also owns the strip's chips and their clears, the control, ad-hoc, sort, and grouping writers, all of them patches on the view's
 * state, and how the strip and the menus name a field. Time predicates re-evaluate on the minute clock so rows age in
 * and out on a quiet desk. On a driver-per-row view the driver whose popover is open stays in the table once the
 * filters no longer match them: opening History may acknowledge their channel, and an "Unacknowledged only" view must
 * not pull the row out from under the panel. On a message-per-row view nothing is exempt, or opening History would
 * grow the table by every message of that driver. The board's task shelf leads the table, its rows kept or hidden by
 * the same filters, or search, as every other row. A change of sort or grouping that changes anything is reported to
 * Amplitude from here, where the value before and after are both known.
 */
export function useDispatchQuery({
  workspaceId,
  definition,
  resolved,
  rows,
  items,
  tagsBySlug,
  tagIdBySlug,
  openMembershipId,
  now,
  unacknowledgedByFeed,
  kindFor,
  tasks,
  knownViewIds,
  write,
}: {
  /** The workspace the page is on, stamped on the events it reports. */
  workspaceId: string;
  definition: DispatchViewDefinition;
  resolved: ResolvedDispatchView;
  rows: readonly DriverRow[];
  items: readonly DispatchItem[];
  tagsBySlug: ReadonlyMap<string, TagDefinition>;
  tagIdBySlug: ReadonlyMap<string, string>;
  /** The driver whose popover is open, exempt from the filters on driver rows. */
  openMembershipId: string | undefined;
  /** The page's minute clock, read only by filters with a time predicate. */
  now: number;
  /** Which channels still wait on a dispatcher, from the acknowledgement stack. */
  unacknowledgedByFeed: ReadonlyMap<string, number>;
  /** A field's kind as the page offers it: while tasks are off, the item kind offers no `task` value. */
  kindFor: (field: DispatchField) => FieldKind;
  /** The workspace's open tasks, whose fields the rows project and whose board's share leads the table. */
  tasks: TaskFold;
  /** Every view the workspace offers; a task pinned to any other is on every board's shelf. */
  knownViewIds: ReadonlySet<string>;
  /** Where every query change goes, as a patch on the view's state. */
  write: (patch: DispatchViewStatePatch) => void;
}): {
  projected: DispatchTableRow[];
  /** The rows the view's filters keep, search aside: the working set the tab badge and title flash watch. */
  filteredByView: DispatchTableRow[];
  /** The rows the table shows: the search's matches while it has a term, else {@link filteredByView}. */
  filtered: DispatchTableRow[];
  grouped: GroupedDispatchRows | null;
  /**
   * The rows of the board's task shelf the view's filters keep, search aside and folded or not: what the badge counts
   * beside the view's own.
   */
  shelfRows: readonly DispatchTableRow[];
  /** The open tasks each shelf row in the table stands for, by row id: what acknowledging that row closes. */
  shelfTasksByRow: ReadonlyMap<string, readonly OpenTask[]>;
  tableRows: DispatchTableRow[];
  total: number;
  controlValues: DispatchControlValues;
  setControl: (id: string, value: DispatchControlValue) => void;
  chips: QueryChip[];
  /** How many chips hide rows: the Filter button's count, and whether the page reads as filtered. */
  filterCount: number;
  /** Whether the search box holds a term, which sets every filter aside until it empties. */
  searching: boolean;
  clearSearch: () => void;
  clearAll: () => void;
  addCondition: (condition: FilterNode) => void;
  replaceConditions: (field: DispatchField, next: FilterNode[]) => void;
  /** The values the ad-hoc conditions on a field name, for an editor to open on. */
  chosenValuesFor: (field: DispatchField) => string[];
  /** Replaces a field's ad-hoc conditions with the values chosen in an editor, or drops them for none. */
  setFieldValues: (field: DispatchField, values: string[]) => void;
  filteredColumnIds: ReadonlySet<string>;
  valueOptionsFor: (control: DispatchControl) => ValueOption[];
  valueOptionsForField: (field: DispatchField) => ValueOption[];
  naming: ConditionNaming;
  search: string;
  setSearch: (search: string) => void;
  setGroupBy: (columnId: string | null) => void;
  toggleGroup: (key: string) => void;
  sortColumn: (columnId: string, direction: "asc" | "desc") => void;
  chooseSort: (next: DisplaySort) => void;
} {
  const { grain, query } = resolved;
  const { sort, adhoc, groupBy, collapsedGroups, controlValues } = query;

  // The board's open tasks, whose rows lead the table wherever they pass the same filters as every other row.
  const shelf = useMemo(
    () => tasksForShelf(tasks.open, definition.id, knownViewIds),
    [tasks, definition.id, knownViewIds],
  );
  const projected = useProjectedRows(grain, rows, items, tagIdBySlug, unacknowledgedByFeed, tasks, shelf);

  const sorted = useMemo(
    () => (sort ? sortDispatchRows(projected, sort, kindFor(sort.field)) : projected),
    [projected, sort, kindFor],
  );

  const [search, setSearch] = useState("");
  const viewId = resolved.id;
  // biome-ignore lint/correctness/useExhaustiveDependencies: reset on the view's identity, not on every object change
  useEffect(() => {
    setSearch("");
  }, [viewId]);

  // The view's filters alone, without the search: what the badge counts, and what returns once the box empties.
  const filterNode = useMemo(() => compileDispatchFilter(query, ""), [query]);
  // A filter without a time predicate leaves the clock out of its dependencies.
  const filterClock = filterNode && hasTimeOperator(filterNode) ? now : null;
  const keepByView = useCallback(
    (shown: DispatchTableRow[]) => keepMatching(shown, filterNode, openMembershipId, filterClock),
    [filterNode, openMembershipId, filterClock],
  );
  const filteredByView = useMemo(() => keepByView(sorted), [keepByView, sorted]);

  // A search sets the filters aside: it matches across every row, and the table shows its matches alone.
  const searching = search.trim() !== "";
  const searchNode = useMemo(() => compileSearchFilter(query, search), [query, search]);
  const keepBySearch = useCallback(
    (shown: DispatchTableRow[]) => keepMatching(shown, searchNode, openMembershipId, null),
    [searchNode, openMembershipId],
  );
  const filtered = useMemo(
    () => (searching ? keepBySearch(sorted) : filteredByView),
    [searching, keepBySearch, sorted, filteredByView],
  );
  const clearSearch = useCallback(() => setSearch(""), []);

  // How the strip and the menus name a field and its values: the view's column label when it has one.
  const naming = useMemo<ConditionNaming>(
    () => ({
      labelFor: (field) => labelForColumnId(field, definition, tagsBySlug),
      kindFor,
    }),
    [definition, tagsBySlug, kindFor],
  );

  const shelveFrom = useMemo(() => {
    const driversById = new Map(rows.map((driver) => [driver.workspaceMembershipId, driver]));
    const itemsById = new Map(items.map((item) => [item.id, item]));
    return (shown: readonly DispatchTableRow[], keep: (rows: DispatchTableRow[]) => DispatchTableRow[]) =>
      shelveTasks({
        grain,
        shown,
        shelf,
        driverRowFor: (id) => driversById.get(id),
        itemFor: (id) => itemsById.get(id),
        tagIdBySlug,
        keep,
      });
  }, [grain, shelf, rows, items, tagIdBySlug]);
  // The shelf under the view's filters, search aside, for the badge; the table's follows the search while it has one.
  const viewShelf = useMemo(
    () => (shelf.length === 0 ? NO_SHELF : shelveFrom(filteredByView, keepByView)),
    [shelf, shelveFrom, filteredByView, keepByView],
  );
  const tableShelf = useMemo(
    () => (searching && shelf.length > 0 ? shelveFrom(filtered, keepBySearch) : viewShelf),
    [searching, shelf, shelveFrom, filtered, keepBySearch, viewShelf],
  );
  const groupRest = useCallback(
    (shown: readonly DispatchTableRow[]): GroupedDispatchRows | null => {
      if (!groupBy) return null;
      return groupDispatchRows(shown, groupBy, kindFor(groupBy), naming.labelFor(groupBy), new Set(collapsedGroups));
    },
    [groupBy, kindFor, naming, collapsedGroups],
  );
  const viewGrouped = useMemo(() => groupRest(filtered), [groupRest, filtered]);
  // With nothing on the shelf the table is the view's alone.
  const withShelf = useMemo(
    () => withTaskShelf({ shelf: tableShelf, shown: filtered, groupRest, collapsed: new Set(collapsedGroups) }),
    [tableShelf, filtered, groupRest, collapsedGroups],
  );
  const grouped = withShelf ?? viewGrouped;

  const setControl = useCallback(
    (id: string, value: DispatchControlValue) => write({ query: { controls: { [id]: value } } }),
    [write],
  );
  // The view's own sort, what a cleared sort returns to; a view that says none has none to return to.
  const viewSort = definition.query.sort === NONE ? undefined : definition.query.sort;
  const chips = useMemo(
    () => buildQueryChips(query, resolved.overrides, naming, write, viewSort),
    [query, resolved.overrides, naming, write, viewSort],
  );
  const filterCount = useMemo(() => narrowingCount(chips), [chips]);
  const clearAll = useCallback(() => {
    setSearch("");
    write(clearAllPatch(query, viewSort));
  }, [write, query, viewSort]);
  const addCondition = useCallback(
    (condition: FilterNode) => write({ query: { adhoc: [...adhoc, condition] } }),
    [write, adhoc],
  );
  // A column's editor owns that column's conditions: applying replaces them, clearing drops them.
  const replaceConditions = useCallback(
    (field: DispatchField, next: FilterNode[]) =>
      write({ query: { adhoc: replaceFieldConditions(adhoc, field, next) } }),
    [write, adhoc],
  );
  const chosenValuesFor = useCallback((field: DispatchField) => chosenValues(adhoc, field), [adhoc]);
  const setFieldValues = useCallback(
    (field: DispatchField, values: string[]) => replaceConditions(field, valuesCondition(field, values)),
    [replaceConditions],
  );
  // The columns whose headers carry the filter mark: one per field an ad-hoc condition is about, and none while a
  // search sets the conditions aside.
  const filteredColumnIds = useMemo(() => {
    if (searching) return NO_COLUMNS;
    const ids = new Set<string>();
    for (const condition of adhoc) {
      const field = conditionField(condition);
      if (field) ids.add(columnIdForConditionField(field));
    }
    return ids;
  }, [adhoc, searching]);
  const valueOptionsForField = useCallback(
    (field: DispatchField) => valueOptions(projected, field, kindFor(field)),
    [projected, kindFor],
  );
  const valueOptionsFor = useCallback(
    (control: DispatchControl) => (control.kind === "values" ? valueOptionsForField(control.field) : []),
    [valueOptionsForField],
  );

  // A column id is the field it shows, so the Display menu's choices write straight into the state.
  const setGroupBy = useCallback(
    (columnId: string | null) => {
      if (columnId !== groupBy) {
        ampli.dispatchGroupByChanged({
          workspaceId,
          fromGroupBy: groupBy ?? undefined,
          toGroupBy: columnId ?? undefined,
        });
      }
      write({ query: { groupBy: columnId ?? NONE } });
    },
    [write, workspaceId, groupBy],
  );
  const toggleGroup = useCallback(
    (key: string) => write({ query: { collapsedGroups: toggleCollapsedGroup(collapsedGroups, key) } }),
    [write, collapsedGroups],
  );

  // Every sort change lands here, from a header's menu or the Display popover, so one place reports it.
  const applySort = useCallback(
    (next: DispatchSort | null) => {
      if (next?.field !== sort?.field || next?.direction !== sort?.direction) {
        ampli.dispatchSortByChanged({
          workspaceId,
          fromSortBy: sort?.field,
          fromSortDirection: sort?.direction,
          toSortBy: next?.field,
          toSortDirection: next?.direction,
        });
      }
      write({ query: { sort: next ?? NONE } });
    },
    [write, workspaceId, sort],
  );
  const sortColumn = useCallback(
    (columnId: string, direction: "asc" | "desc") => applySort({ field: columnId, direction }),
    [applySort],
  );
  // A freshly chosen field starts in the direction its kind suggests; a repeat of the current field keeps the direction
  // the popover shows.
  const chooseSort = useCallback(
    (next: DisplaySort) => {
      if (!next) {
        applySort(null);
        return;
      }
      const sameField = sort != null && sort.field === next.key;
      const direction = sameField ? next.direction : defaultSortDirection(kindFor(next.key));
      applySort({ field: next.key, direction });
    },
    [applySort, kindFor, sort],
  );

  return {
    projected,
    filteredByView,
    filtered,
    grouped,
    shelfRows: viewShelf.rows,
    shelfTasksByRow: tableShelf.tasksByRow,
    tableRows: grouped ? grouped.rows : filtered,
    total: projected.length,
    controlValues,
    setControl,
    chips,
    filterCount,
    clearAll,
    addCondition,
    replaceConditions,
    chosenValuesFor,
    setFieldValues,
    filteredColumnIds,
    valueOptionsFor,
    valueOptionsForField,
    naming,
    search,
    setSearch,
    searching,
    clearSearch,
    setGroupBy,
    toggleGroup,
    sortColumn,
    chooseSort,
  };
}

/**
 * {@link projectRows} across renders, reusing last pass's rows: an unchanged driver keeps its row object, and a pass
 * that changed no row returns last pass's array, so the sort, filter and table memos downstream all skip.
 */
function useProjectedRows(
  grain: ResolvedDispatchView["grain"],
  rows: readonly DriverRow[],
  items: readonly DispatchItem[],
  tagIdBySlug: ReadonlyMap<string, string>,
  unacknowledgedByFeed: ReadonlyMap<string, number>,
  tasks: TaskFold,
  shelf: readonly OpenTask[],
): DispatchTableRow[] {
  const previousRef = useRef({
    grain,
    tagIdBySlug,
    rows: [] as DispatchTableRow[],
    byKey: NO_PREVIOUS,
  });
  return useMemo(() => {
    const last = previousRef.current;
    // A new grain or tag set changes every record, so nothing from the last pass is reusable.
    const reusable = last.grain === grain && last.tagIdBySlug === tagIdBySlug;
    const previous = reusable ? last.byKey : NO_PREVIOUS;
    const next = projectRows(grain, rows, items, tagIdBySlug, unacknowledgedByFeed, tasks, previous, shelf);
    const kept = keepEqualRows(reusable ? last.rows : [], previous, next, rowId);
    previousRef.current = { grain, tagIdBySlug, ...kept };
    return kept.rows;
  }, [grain, rows, items, tagIdBySlug, unacknowledgedByFeed, tasks, shelf]);
}
